import {
  ChevronDownIcon,
  GripVerticalIcon,
  PenLineIcon,
  XIcon,
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'

import { TextareaField } from '@/components/ui'
import {
  useWizardActions,
  useWizardSectionInstructions,
} from '@/modules/analysis'
import type { ConfigSection } from '@/modules/projects'
import { cn } from '@/utils/ui'
import { SectionIcon } from './SectionIcon'

interface SectionItemProps {
  section: ConfigSection
  onDragStart?: (key: ConfigSection['key']) => void
  isPinned?: boolean
}

export const SectionItem = ({
  section,
  onDragStart,
  isPinned = false,
}: SectionItemProps) => {
  const sectionInstructions = useWizardSectionInstructions()
  const { removeSection, setSectionInstruction } = useWizardActions()
  const hasInstruction =
    (sectionInstructions[section.key] ?? '').trim().length > 0
  const [isExpanded, setIsExpanded] = useState(hasInstruction)

  const handleDragStart = (event: React.DragEvent) => {
    event.dataTransfer.setData('text/plain', section.key)
    event.dataTransfer.effectAllowed = 'move'
    onDragStart?.(section.key)
  }

  return (
    <div
      className="border-b border-border/50 last:border-b-0"
      draggable={!isPinned}
      onDragStart={!isPinned ? handleDragStart : undefined}
    >
      <div className="flex items-center gap-2.5 px-4 py-3">
        {!isPinned && (
          <>
            <GripVerticalIcon className="h-4 w-4 shrink-0 text-muted-foreground/40 cursor-grab active:cursor-grabbing" />
            <SectionIcon sectionKey={section.key} />
          </>
        )}

        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium">{section.label}</span>
          <p className="text-xs text-muted-foreground">{section.description}</p>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {!isPinned && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className={cn(
                'flex items-center gap-1.5 text-xs font-medium transition-colors px-2 py-1 rounded-md border cursor-pointer',
                hasInstruction
                  ? 'text-brand border-brand/30 bg-brand/5'
                  : 'text-muted-foreground border-border hover:text-foreground hover:border-foreground/20',
              )}
            >
              <PenLineIcon className="h-3 w-3" />
              <span>{hasInstruction ? 'Edit focus' : 'Customize focus'}</span>
              <ChevronDownIcon
                className={cn(
                  'h-3 w-3 transition-transform',
                  isExpanded && 'rotate-180',
                )}
              />
            </button>
          )}

          {!isPinned && (
            <button
              type="button"
              onClick={() => removeSection(section.key)}
              className="text-muted-foreground/40 hover:text-red-500 transition-colors p-1 rounded cursor-pointer"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && !isPinned && (
          <motion.div
            key="instructions"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="overflow-hidden pt-px"
          >
            <div className="px-4 pb-3 pl-9">
              <TextareaField
                label={`Instructions for ${section.label}`}
                className="[&_label]:sr-only"
                id={`instruction-${section.key}`}
                placeholder={section.placeholder}
                value={sectionInstructions[section.key] ?? ''}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setSectionInstruction(section.key, event.target.value)
                }
                rows={2}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
